/** VISTOERA_CANVAS_CANONICAL_HOVER_QUICK_ACTION_ADMIN_DRIVEN_NEXT_NODE_
 * CONTEXT_HANDOFF_P0_V1 — canonical "next-step" Hover Quick Action rail
 * for any Generation / Asset Node's Result region.
 *
 * The ONE rail every Canvas node uses (TASK-00584: `MediaWorkflowRail`,
 * `AudioWorkflowRail`, `VoiceTemplateWorkflowRail` and
 * `MotionResultWorkflowRail` are now thin wrappers over this component,
 * so no node keeps a second, hardcoded action list).
 *
 * Same visual/positioning contract as before (spec Gate A/B): reuses the
 * `pcv-media-workflow-toolbar` / `pcv-media-workflow-btn` CSS (circular,
 * icon-only, positioned outside the Result region to the right,
 * `position: absolute; left: 100%`), rendered as a sibling right before
 * the node root closes (never nested inside Result content — spec Gate
 * E "由 RESULT DOM OWN POSITIONING", never a JS-measured offset).
 *
 * The row list is the live, Admin-driven, ONE-fetch-per-workspace cache
 * (`canvasQuickActionRuntime.ts`) — enable/disable + `sort_order` here
 * always mirrors Platform Admin's "功能节点管理 → 快捷动作" panel
 * exactly (spec Gate G), never a per-node hardcoded kind array.
 *
 * Final visibility = Admin enabled AND this source node implements a
 * handler for the target AND the target is creatable for the tenant
 * (node catalog: availability / enabled / READY). Admin enablement can
 * never bypass the catalog / entitlement check.
 *
 * `actionHandlers` supplies ONLY the click callback per legal
 * `target_node_type` this source node actually implements today — the
 * rail itself never invents a target/handler; an admin-enabled action
 * with no matching handler here simply does not render (fail-closed,
 * never a dead button). Config not loaded / load failed => renders
 * nothing (no flicker, no "show all" fallback).
 *
 * Execution guard: on click the CURRENT config is re-checked, so an
 * action disabled after this rail rendered (or a cached handler) can
 * never run through this entry. */
import { Tooltip } from 'antd'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { useSyncExternalStore } from 'react'
import { resolveVisibleQuickActionRows } from '../canvasQuickActionResolution'
import {
  getQuickActionConfigRevision,
  getEnabledQuickActionsForSource,
  isQuickActionEnabled,
  subscribeQuickActionConfig,
} from '../canvasQuickActionRuntime'
import { getAuthMatrixRevision, subscribeAuthMatrix } from '../authMatrixRuntime'
import { getNodeCatalogRevision, isNodeCreatableInCatalog, subscribeNodeCatalog } from '../nodeCatalogRuntime'
import { useNodeDefinition } from '../nodeRegistry'

export interface QuickActionHandler {
  targetNodeType: string
  onClick: (e: ReactMouseEvent) => void
  /** Overrides the Admin-configured label (rare — defaults to it). */
  tooltip?: string
  /** Non-config precondition (e.g. stale audio). Never overrides Admin disable. */
  disabled?: boolean
  disabledReason?: string
  testId?: string
  ariaLabel?: string
  /** Defaults to the target node's canonical icon. */
  icon?: ReactNode
}

interface Props {
  sourceNodeType: string
  actionHandlers: QuickActionHandler[]
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  className?: string
  railTestId?: string
  /** Legacy image/audio/motion rails render the bare target icon (no node-colour tint). */
  plainIcons?: boolean
}

function TargetIcon({ targetNodeType, plain }: { targetNodeType: string; plain?: boolean }) {
  const def = useNodeDefinition(targetNodeType)
  return plain ? <>{def.icon}</> : <span style={{ color: def.color }}>{def.icon}</span>
}

export default function CanvasResultQuickActionRail({
  sourceNodeType,
  actionHandlers,
  onMouseEnter,
  onMouseLeave,
  className,
  railTestId,
  plainIcons,
}: Props) {
  useSyncExternalStore(subscribeQuickActionConfig, getQuickActionConfigRevision, getQuickActionConfigRevision)
  useSyncExternalStore(subscribeNodeCatalog, getNodeCatalogRevision, getNodeCatalogRevision)
  useSyncExternalStore(subscribeAuthMatrix, getAuthMatrixRevision, getAuthMatrixRevision)

  const handlersByTarget = new Map(actionHandlers.map((h) => [h.targetNodeType, h]))
  const rows = resolveVisibleQuickActionRows(getEnabledQuickActionsForSource(sourceNodeType), actionHandlers, isNodeCreatableInCatalog)

  if (!rows.length) return null

  return (
    <div
      className={`pcv-media-workflow-toolbar nodrag${className ? ` ${className}` : ''}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-testid={railTestId ?? 'canvas-result-quick-action-rail'}
    >
      {rows.map((row) => {
        const handler = handlersByTarget.get(row.target_node_type)
        if (!handler) return null
        const label = handler.tooltip ?? row.label
        const title = handler.disabled && handler.disabledReason ? handler.disabledReason : label
        return (
          <Tooltip key={row.action_key} title={title} placement="right" mouseEnterDelay={0.3}>
            <button
              type="button"
              className="pcv-media-workflow-btn"
              aria-label={handler.ariaLabel ?? label}
              data-testid={handler.testId ?? `quick-action-${row.action_key}`}
              data-quick-action-key={row.action_key}
              disabled={handler.disabled}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                if (handler.disabled) return
                // Execution guard: re-check the CURRENT Admin config.
                if (!isQuickActionEnabled(sourceNodeType, row.target_node_type)) return
                handler.onClick(e)
              }}
            >
              {handler.icon ?? <TargetIcon targetNodeType={row.target_node_type} plain={plainIcons} />}
            </button>
          </Tooltip>
        )
      })}
    </div>
  )
}
